import * as React from 'react';

import { FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import InputRadio from 'components/inputs/input-radio/InputRadio';

import { __t, applyTranslations } from 'utils/language';
import {
  SHORT_STRING_MAX_LENGTH,
  isBooleanRequired,
  isRequired,
  isUrl,
  maxLength
} from 'utils/validators';

import './profile-agency.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./profile-agency.i18n.json'));

import type { Agency, InfluencerProfileForm } from 'types';

interface Props {
  profile: InfluencerProfileForm,
  onChange: (profile: Partial<InfluencerProfileForm>) => void
}

const IS_AGENCY_MANAGED_OPTIONS = ['yes', 'no'];
const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'profile_agency';

const ProfileAgency: React.FC<Props> = ({
  profile,
  onChange
}) => {
  const getStatus = (
    option: string
  ): boolean => (
    (option === IS_AGENCY_MANAGED_OPTIONS[0] && profile.isAgencyManaged)
    || option === IS_AGENCY_MANAGED_OPTIONS[1] && profile.isAgencyManaged === false
  );

  const handleRadioChange = (
    event: React.SyntheticEvent<HTMLInputElement>
  ): void => {
    const target = event.currentTarget;

    onChange({
      [target.name]: target.value === 'yes'
    });
  };

  const handleValueChange = (
    event: React.SyntheticEvent<HTMLInputElement>
  ): void => {
    const target = event.currentTarget;
    const name = target.name === 'agencyName'
      ? 'name'
      : 'url';

    const agencyDetails = {
      ...profile.agencyDetails,
      [name]: target.value
    } as Agency;

    onChange({
      agencyDetails
    });
  };

  const renderDetails = (): React.ReactNode => {
    const name = profile.agencyDetails?.name ?? '';
    const website = profile.agencyDetails?.url ?? '';

    return (
      <div className="profile-agency__details">
        <FormField
          label={__t(`${MSG_KEY}.labels.agency_name`)}
          labelType="highlighted"
          direction="column"
          showValidationIcon={false}
          showRequiredMark
          className="profile-agency__field"
          tooltipMessage={__t(`${MSG_KEY}.tooltips.agency_name`)}
          tooltipWidth="normal"
          tooltipTextAlign="left"
          validators={[
            isRequired(name ?? 'ok'),
            maxLength(name, SHORT_STRING_MAX_LENGTH)
          ]}
        >
          <Input
            value={name}
            name="agencyName"
            width="responsive"
            onChange={handleValueChange}
          />
        </FormField>
        <FormField
          label={__t(`${MSG_KEY}.labels.agency_website`)}
          labelType="highlighted"
          direction="column"
          showValidationIcon={false}
          showRequiredMark
          className="profile-agency__field"
          validators={[
            isRequired(website ?? 'ok'),
            isUrl(website)
          ]}
        >
          <Input
            value={website}
            name="agencyWebsite"
            width="responsive"
            onChange={handleValueChange}
          />
        </FormField>
      </div>
    );
  };

  return (
    <div className="profile-agency">
      <FormField
        label={__t(`${MSG_KEY}.labels.managed_by_agency`)}
        labelType="highlighted"
        direction="column"
        showRequiredMark
        showValidationIcon={false}
        className="profile-agency__field"
        validators={[
          isBooleanRequired(profile.isAgencyManaged)
        ]}
      >
        {
          IS_AGENCY_MANAGED_OPTIONS.map(option => (
            <InputRadio
              key={`is-agency-managed-${option}`}
              id={`is-agency-managed-${option}`}
              name="isAgencyManaged"
              value={option}
              checked={getStatus(option)}
              label={__t(`${COMMON_MSG_KEY}.basics.${option}`)}
              onChange={handleRadioChange}
            />
          ))
        }
      </FormField>
      {profile.isAgencyManaged && renderDetails()}
    </div>
  );
};

export default ProfileAgency;
